import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';
import Button from '../../../components/ui/Button';
import Input from '../../../components/ui/Input';

const PaymentMethods = ({ paymentMethods, onAddPaymentMethod, onDeletePaymentMethod, onSetDefaultPayment }) => {
  const [showAddForm, setShowAddForm] = useState(false);
  const [formData, setFormData] = useState({
    type: 'credit',
    cardNumber: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    cardholderName: '',
    billingAddress: ''
  });

  const handleInputChange = (e) => {
    const { name, value } = e?.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = (e) => {
    e?.preventDefault();
    onAddPaymentMethod(formData);
    setShowAddForm(false);
    setFormData({
      type: 'credit',
      cardNumber: '',
      expiryMonth: '',
      expiryYear: '',
      cvv: '',
      cardholderName: '',
      billingAddress: ''
    });
  };

  const getCardIcon = (type, brand) => {
    if (brand) {
      switch (brand?.toLowerCase()) {
        case 'visa':
          return 'CreditCard';
        case 'mastercard':
          return 'CreditCard';
        case 'amex':
          return 'CreditCard';
        default:
          return 'CreditCard';
      }
    }
    return type === 'credit' ? 'CreditCard' : 'Wallet';
  };

  const getCardColor = (brand) => {
    switch (brand?.toLowerCase()) {
      case 'visa':
        return 'text-blue-500';
      case 'mastercard':
        return 'text-red-500';
      case 'amex':
        return 'text-green-500';
      default:
        return 'text-muted-foreground';
    }
  };

  const maskCardNumber = (number) => {
    return `**** **** **** ${number?.slice(-4)}`;
  };

  return (
    <div className="holographic-card p-6 rounded-lg">
      <div className="flex items-center justify-between mb-6">
        <h2 className="text-xl font-bold command-text">Payment Methods</h2>
        <Button
          variant="default"
          size="sm"
          onClick={() => setShowAddForm(true)}
          iconName="Plus"
          iconPosition="left"
        >
          Add Payment Method
        </Button>
      </div>
      {/* Add Payment Method Form */}
      {showAddForm && (
        <div className="mb-6 p-4 border border-border rounded-lg bg-muted/20">
          <h3 className="text-lg font-semibold mb-4">Add New Payment Method</h3>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2">Payment Type</label>
              <select
                name="type"
                value={formData?.type}
                onChange={handleInputChange}
                className="w-full px-3 py-2 bg-input border border-border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/50"
              >
                <option value="credit">Credit Card</option>
                <option value="debit">Debit Card</option>
                <option value="paypal">PayPal</option>
              </select>
            </div>

            {formData?.type !== 'paypal' && (
              <>
                <Input
                  label="Card Number"
                  name="cardNumber"
                  value={formData?.cardNumber}
                  onChange={handleInputChange}
                  placeholder="1234 5678 9012 3456"
                  required
                />

                <Input
                  label="Cardholder Name"
                  name="cardholderName"
                  value={formData?.cardholderName}
                  onChange={handleInputChange}
                  required
                />

                <div className="grid grid-cols-3 gap-4">
                  <Input
                    label="Expiry Month"
                    name="expiryMonth"
                    value={formData?.expiryMonth}
                    onChange={handleInputChange}
                    placeholder="MM"
                    required
                  />
                  <Input
                    label="Expiry Year"
                    name="expiryYear"
                    value={formData?.expiryYear}
                    onChange={handleInputChange}
                    placeholder="YYYY"
                    required
                  />
                  <Input
                    label="CVV"
                    name="cvv"
                    value={formData?.cvv}
                    onChange={handleInputChange}
                    placeholder="123"
                    required
                  />
                </div>
              </>
            )}

            <div className="flex space-x-2">
              <Button type="submit" variant="default">
                Add Payment Method
              </Button>
              <Button 
                type="button" 
                variant="ghost" 
                onClick={() => setShowAddForm(false)}
              >
                Cancel
              </Button>
            </div>
          </form>
        </div>
      )}
      {/* Payment Methods List */}
      <div className="space-y-4">
        {paymentMethods?.length === 0 ? (
          <div className="text-center py-8">
            <Icon name="CreditCard" size={48} className="text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground">No payment methods saved yet</p>
          </div>
        ) : (
          paymentMethods?.map((method) => (
            <div
              key={method?.id}
              className={`border rounded-lg p-4 transition-all duration-200 ${
                method?.isDefault
                  ? 'border-primary bg-primary/5' :'border-border hover:bg-muted/20'
              }`}
            >
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className={`p-3 rounded-lg ${
                    method?.isDefault ? 'bg-primary/20' : 'bg-muted'
                  }`}>
                    <Icon 
                      name={getCardIcon(method?.type, method?.brand)} 
                      size={24} 
                      className={method?.isDefault ? 'text-primary' : getCardColor(method?.brand)}
                    />
                  </div>
                  <div>
                    <div className="flex items-center space-x-2 mb-1">
                      <h3 className="font-semibold">
                        {method?.brand ? method?.brand?.toUpperCase() : method?.type?.toUpperCase()}
                      </h3>
                      {method?.isDefault && (
                        <span className="px-2 py-1 bg-primary/20 text-primary text-xs rounded-full border border-primary/30">
                          Default
                        </span>
                      )}
                    </div>
                    {method?.type !== 'paypal' ? (
                      <>
                        <p className="text-sm text-muted-foreground">
                          {maskCardNumber(method?.cardNumber)}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          Expires {method?.expiryMonth}/{method?.expiryYear}
                        </p>
                        <p className="text-sm text-muted-foreground">
                          {method?.cardholderName}
                        </p>
                      </>
                    ) : (
                      <p className="text-sm text-muted-foreground">
                        {method?.email}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  {!method?.isDefault && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onSetDefaultPayment(method?.id)}
                      iconName="Star"
                      iconPosition="left"
                    >
                      Set Default
                    </Button>
                  )}
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => onDeletePaymentMethod(method?.id)}
                    iconName="Trash2"
                    iconPosition="left"
                    className="text-error hover:text-error"
                  >
                    Remove
                  </Button>
                </div>
              </div>
            </div>
          ))
        )}
      </div>
      {/* Security Notice */}
      <div className="mt-6 p-4 bg-success/10 border border-success/30 rounded-lg">
        <div className="flex items-start space-x-3">
          <Icon name="Shield" size={20} className="text-success mt-0.5" />
          <div>
            <h4 className="font-medium text-success mb-1">Secure Payment Processing</h4>
            <p className="text-sm text-muted-foreground">
              All payment information is encrypted and securely stored. We never store your full card details on our servers.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PaymentMethods;